import React, { useState, useRef, useEffect, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Button } from "../../../components/Button";
import SearchComponent from "../../../components/Search";

import { api } from "../../../helpers/api";
import { Loader } from "../../../components/Loader";
import TableSkeleton from "./TableSkeleton";

type CourseSelectionProps = {
  loading: boolean;
  onBack: () => void;
  onSubmit: (selectedCourses: any[]) => void;
  selectedCourses: any[];
};

export function CourseSelection({
  loading,
  onBack,
  onSubmit,
  selectedCourses: initialSelectedCourses,
}: CourseSelectionProps) {
  const [search, setSearch] = useState("");
  const [selectedCourses, setSelectedCourses] = useState(
    initialSelectedCourses
  );
  const [tableData, setTableData] = useState<any[]>([]);
  const loadMoreRef = useRef<any>(null);

  const fetchContents = async ({ pageParam }) => {
    const res = await api.getContents({
      page: pageParam,
      limit: 10,
      keyword: search,
      type: "all",
    });
    return res;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["contents", search],
    queryFn: fetchContents,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages, pageParam) => {
      if (lastPage?.data?.publisherVideo?.length < 10) return null;
      return pageParam + 1;
    },
  });

  // Combine pages into table data
  useMemo(() => {
    if (data) {
      const newData = data.pages.flatMap((page) => page?.data?.publisherVideo);
      setTableData(newData);
    }
  }, [data]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 1.0,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const toggleCourse = (course: any) => {
    if (loading) return;
    setSelectedCourses((prev) =>
      prev.some((c) => c.video_id === course.video_id)
        ? prev.filter((c) => c.video_id !== course.video_id)
        : [...prev, course]
    );
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <SearchComponent
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search courses"
        disabled={loading}
      />

      <div className="max-h-[400px] overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center items-center h-[200px]">
            <Loader size="large" className="text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {!isLoading &&
              tableData.map((course) => (
                <div
                  key={course.video_id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                    selectedCourses.some(
                      (c) => c?.video_id === course?.video_id
                    )
                      ? "border-primary bg-primary/5"
                      : ""
                  } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => toggleCourse(course)}
                >
                  <img
                    src={course.thumb_image}
                    alt={course.title}
                    className="w-16 h-16 rounded object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-medium">{course.title}</h3>
                    <p className="text-sm text-gray-500">
                      {course.lessons_count} lessons
                    </p>
                    <p className="text-xs text-gray-400">
                      By {course.publisher_data?.full_name}
                    </p>
                  </div>
                </div>
              ))}
            {isFetching && !isLoading && <TableSkeleton rows={2} columns={2} />}
          </div>
        )}

        {!isLoading && !isFetching && tableData.length === 0 && (
          <div className="flex-1 flex justify-center items-center h-[200px]">
            <span className="text-[#999999] text-[0.875rem] font-medium">
              No courses available
            </span>
          </div>
        )}

        <div ref={loadMoreRef}>
          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader size="small" className="text-primary" />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between mt-4">
        <Button variant="outline" onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onSubmit(selectedCourses);
          }}
          disabled={selectedCourses.length === 0 || loading}
        >
          {loading
            ? "Saving..."
            : `Create Playlist (${selectedCourses.length} courses)`}
        </Button>
      </div>
    </div>
  );
}
